import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import Layout from './Layout';
import YearSelector from './YearSelector';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { FileText, Plus, Search, Check, AlertTriangle, Edit, Trash2, Upload, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../utils/config';
import { InvoiceCompanySelect } from './InvoiceCompanySelect';

const API = `${BACKEND_URL}/api`;

const InvoicesManager = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState('');
  const [categories, setCategories] = useState([]);
  const [budgetLines, setBudgetLines] = useState([]);
  const [filteredBudgetLines, setFilteredBudgetLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('all'); // all, passive, active
  const [showWithoutCategory, setShowWithoutCategory] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showEditInvoice, setShowEditInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [newInvoice, setNewInvoice] = useState({
    supplier: '',
    invoice_number: '',
    amount: 0,
    invoice_date: '',
    due_date: '',
    status: 'pending',
    invoice_type: 'passive', // passive or active
    description: '',
    company_id: '',
    budget_line_id: '',
    property_id: '',
    contact_nature: '',
    supplier_iban: '',
    supplier_email: ''
  });
  const [newBudgetLineName, setNewBudgetLineName] = useState('');
  const [editingBudgetLineId, setEditingBudgetLineId] = useState(null);
  const [editingBudgetLineName, setEditingBudgetLineName] = useState('');
  // Autocomplete rubrica beneficiari (form Nuova Fattura)
  const [contactResults, setContactResults] = useState([]);
  const [contactMatch, setContactMatch] = useState(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);

  // Ricerca intelligente in rubrica pagamenti mentre si digita il fornitore
  useEffect(() => {
    if ((!showNewInvoice && !showEditInvoice) || newInvoice.invoice_type !== 'passive') { setContactResults([]); return; }
    const q = (newInvoice.supplier || '').trim();
    if (q.length < 2) { setContactResults([]); setShowContactDropdown(false); return; }
    if (contactMatch && contactMatch.name === q) return;
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('hw_token');
        const r = await axios.get(`${API}/payment-contacts?search=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContactResults((r.data.contacts || []).slice(0, 8));
        setShowContactDropdown(true);
      } catch (_) { setContactResults([]); }
    }, 350);
    return () => clearTimeout(timer);
  }, [newInvoice.supplier, newInvoice.invoice_type, showNewInvoice, showEditInvoice]);

  useEffect(() => {
    fetchInvoices();
    fetchProperties();
    fetchCompanies();
    fetchCategories();
    fetchBudgetLines(); // Fetch all budget lines initially
  }, []);

  useEffect(() => {
    // Reload invoices when year changes
    fetchInvoices();
  }, [currentYear]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/invoices?year=${currentYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    setCompaniesError('');
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/invoice-context/company-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!Array.isArray(response.data?.items)) throw new Error('Formato elenco società non valido');
      setCompanies(response.data.items);
    } catch (error) {
      setCompaniesError(error.response?.status === 403
        ? 'Questo account non è autorizzato a caricare le società per le fatture.'
        : 'Impossibile caricare le società. Riprova.');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBudgetLines = async (companyId = null) => {
    try {
      const token = localStorage.getItem('hw_token');
      const url = companyId 
        ? `${API}/budget-lines?company_id=${companyId}`
        : `${API}/budget-lines`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only EXPENSE budget lines for invoices
      const expenseLines = response.data.filter(line => line.type === 'expense');
      setBudgetLines(expenseLines);
      return expenseLines;
    } catch (error) {
      console.error('Error fetching budget lines:', error);
      return [];
    }
  };

  // Filter budget lines when company changes
  const handleCompanyChange = async (companyId) => {
    setNewInvoice({...newInvoice, company_id: companyId, budget_line_id: ''});
    if (companyId) {
      const lines = await fetchBudgetLines(companyId);
      setFilteredBudgetLines(lines);
    } else {
      setFilteredBudgetLines([]);
    }
  };

  const resolveContactDecision = async (supplierName, iban) => {
    // Conferma esplicita prima di associare un beneficiario per SOMIGLIANZA del nome
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(`${API}/payment-contacts/match-preview`,
        { name: supplierName, iban: iban || '' },
        { headers: { Authorization: `Bearer ${token}` } });
      const { match_type, contact } = res.data || {};
      if (match_type === 'similar' && contact) {
        const ok = window.confirm(
          `⚠️ CONFERMA ASSOCIAZIONE BENEFICIARIO\n\n` +
          `Il nome "${supplierName}" ASSOMIGLIA a un beneficiario già in rubrica:\n\n` +
          `   ${contact.name}${contact.iban ? `\n   IBAN: ${contact.iban}` : ''}\n\n` +
          `OK = ASSOCIA a "${contact.name}" (i pagamenti useranno il suo IBAN)\n` +
          `ANNULLA = crea un NUOVO beneficiario separato "${supplierName}"`
        );
        return ok ? `use:${contact.id}` : 'new';
      }
    } catch (_) { /* rubrica non disponibile: procedi in automatico */ }
    return '';
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    if (!newInvoice.company_id) {
      alert('Seleziona società');
      return;
    }
    if (newInvoice.invoice_type === 'passive' && !newInvoice.contact_nature) {
      alert('Seleziona la Natura del rapporto (Fornitore, Partner, Responsabile o Dipendente)');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const contactDecision = (newInvoice.invoice_type === 'passive' && newInvoice.supplier)
        ? await resolveContactDecision(newInvoice.supplier, newInvoice.supplier_iban)
        : '';
      // Create invoice first
      const response = await axios.post(`${API}/invoices`, {
        ...newInvoice,
        supplier_contact_decision: contactDecision,
        invoice_date: new Date(newInvoice.invoice_date).toISOString(),
        due_date: new Date(newInvoice.due_date).toISOString(),
        amount: parseFloat(newInvoice.amount),
        property_id: (newInvoice.property_id && newInvoice.property_id !== "none") ? newInvoice.property_id : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const createdInvoice = response.data;
      
      // Upload attachment if file is selected
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('file', attachmentFile);
        
        await axios.post(`${API}/invoices/${createdInvoice.id}/upload-attachment`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Create recurring cost if flagged
      if (newInvoice.is_recurring && newInvoice.invoice_type === 'passive') {
        try {
          await axios.post(`${API}/recurring-costs`, {
            supplier_name: newInvoice.supplier,
            amount: parseFloat(newInvoice.amount),
            day_of_month: newInvoice.recurring_day || 10,
            description: newInvoice.description || `Costo fisso mensile ${newInvoice.supplier}`,
            iban: (newInvoice.recurring_iban || '').trim(),
            xml_name: (newInvoice.recurring_xml_name || '').trim(),
            category: newInvoice.recurring_category || 'fornitore',
            company_id: newInvoice.company_id,
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (rcErr) {
          console.error('Error creating recurring cost:', rcErr);
        }
      }
      
      alert('Fattura creata con successo!' + (newInvoice.is_recurring ? ' Costo ricorrente salvato.' : ''));
      setShowNewInvoice(false);
      setAttachmentFile(null);
      setContactMatch(null);
      setContactResults([]);
      setNewInvoice({
        supplier: '',
        invoice_number: '',
        amount: 0,
        invoice_date: '',
        due_date: '',
        status: 'pending',
        invoice_type: 'passive',
        description: '',
        company_id: '',
        budget_line_id: '',
        property_id: '',
        contact_nature: '',
        supplier_iban: '',
        supplier_email: '',
        is_recurring: false,
        recurring_iban: '',
        recurring_xml_name: '',
        recurring_day: 10,
        recurring_category: 'fornitore',
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione della fattura';
      alert(errorMsg);
    }
  };

  const handleNewInvoiceOpenChange = (open) => {
    if (open) {
      // Reset completo: evita che restino i dati di una fattura aperta in Modifica
      setEditingInvoice(null);
      setContactMatch(null);
      setContactResults([]);
      setAttachmentFile(null);
      setNewInvoice({
        supplier: '',
        invoice_number: '',
        amount: 0,
        invoice_date: '',
        due_date: '',
        status: 'pending',
        invoice_type: 'passive',
        description: '',
        company_id: '',
        budget_line_id: '',
        property_id: '',
        contact_nature: '',
        supplier_iban: '',
        supplier_email: ''
      });
    }
    setShowNewInvoice(open);
  };

  const handleEditInvoice = async (invoice) => {
    setEditingInvoice(invoice);
    setContactMatch(null);
    setContactResults([]);
    setNewInvoice({
      supplier: invoice.supplier,
      invoice_number: invoice.invoice_number,
      amount: invoice.amount,
      invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
      status: invoice.status,
      invoice_type: invoice.invoice_type || 'passive',
      description: invoice.description || '',
      company_id: invoice.company_id,
      budget_line_id: invoice.budget_line_id || '',
      property_id: invoice.property_id || '',
      contact_nature: ['fornitore', 'partner', 'responsabile', 'dipendente', 'segnalatore'].includes(invoice.category) ? invoice.category : '',
      supplier_iban: '',
      supplier_email: invoice.supplier_email || ''
    });
    // Cerca il contatto in rubrica per mostrare IBAN/email già presenti
    try {
      const token = localStorage.getItem('hw_token');
      const r = await axios.get(`${API}/payment-contacts?search=${encodeURIComponent(invoice.supplier || '')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const exact = (r.data.contacts || []).find(
        c => (c.name || '').trim().toLowerCase() === (invoice.supplier || '').trim().toLowerCase()
      );
      if (exact) {
        setContactMatch(exact);
        if (['fornitore', 'partner', 'responsabile', 'dipendente', 'segnalatore'].includes(exact.category)) {
          setNewInvoice(prev => ({ ...prev, contact_nature: prev.contact_nature || exact.category }));
        }
      }
    } catch (_) { /* rubrica non disponibile: campi comunque editabili */ }
    // Carica budget lines per la company selezionata
    if (invoice.company_id) {
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.get(`${API}/budget-lines?company_id=${invoice.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const expenseLines = (res.data || []).filter(line => line.type === 'expense');
        setFilteredBudgetLines(expenseLines);
      } catch (err) {
        console.error('Error loading budget lines for edit:', err);
        setFilteredBudgetLines([]);
      }
    }
    setAttachmentFile(null);
    setShowEditInvoice(true);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    
    if (!editingInvoice) return;
    if (newInvoice.invoice_type === 'passive' && !newInvoice.contact_nature) {
      alert('Seleziona la Natura del rapporto (Fornitore, Partner, Responsabile o Dipendente)');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const contactDecisionU = (newInvoice.invoice_type === 'passive' && newInvoice.supplier)
        ? await resolveContactDecision(newInvoice.supplier, newInvoice.supplier_iban)
        : '';
      // Update invoice
      await axios.put(`${API}/invoices/${editingInvoice.id}`, {
        ...newInvoice,
        supplier_contact_decision: contactDecisionU,
        invoice_date: new Date(newInvoice.invoice_date).toISOString(),
        due_date: new Date(newInvoice.due_date).toISOString(),
        amount: parseFloat(newInvoice.amount),
        property_id: (newInvoice.property_id && newInvoice.property_id !== "none") ? newInvoice.property_id : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload attachment if file is selected
      if (attachmentFile) {
        const formData = new FormData();
        formData.append('file', attachmentFile);
        
        await axios.post(`${API}/invoices/${editingInvoice.id}/upload-attachment`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      alert('Fattura aggiornata con successo! Le modifiche si rifletteranno automaticamente nel Controllo di Gestione.');
      setShowEditInvoice(false);
      setEditingInvoice(null);
      setAttachmentFile(null);
      setContactMatch(null);
      setContactResults([]);
      setNewInvoice({
        supplier: '',
        invoice_number: '',
        amount: 0,
        invoice_date: '',
        due_date: '',
        status: 'pending',
        invoice_type: 'passive',
        description: '',
        company_id: '',
        budget_line_id: '',
        property_id: '',
        contact_nature: '',
        supplier_iban: '',
        supplier_email: ''
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'aggiornamento della fattura';
      alert(errorMsg);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fattura?')) {
      return;
    }
    if (!window.confirm('⚠️ CONFERMA DEFINITIVA\n\nLa fattura verrà eliminata per sempre e non potrà essere recuperata.\n\nProcedere con l\'eliminazione?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Fattura eliminata con successo!');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'eliminazione della fattura';
      alert(errorMsg);
    }
  };

  const refreshBudgetLines = async () => {
    if (!newInvoice.company_id) return [];
    const lines = await fetchBudgetLines(newInvoice.company_id);
    setFilteredBudgetLines(lines);
    return lines;
  };

  const handleCreateBudgetLine = async (e) => {
    e.preventDefault();
    const name = newBudgetLineName.trim();
    if (!name) {
      toast.error('Inserisci il nome della voce di costo');
      return;
    }
    if (!newInvoice.company_id) {
      toast.error('Prima seleziona una società');
      return;
    }
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/budget-lines`, {
        company_id: newInvoice.company_id,
        name,
        type: 'expense'
      }, { headers: { Authorization: `Bearer ${token}` } });

      const created = response.data?.budget_line;
      toast.success('Voce di costo aggiunta');
      setNewBudgetLineName('');

      if (created?.id) {
        // Optimistically add the new line to the options FIRST and let Radix
        // SelectItem mount/register its label, THEN set the value on the next
        // tick so SelectValue can resolve id -> label correctly.
        setFilteredBudgetLines(prev => (prev.some(l => l.id === created.id) ? prev : [...prev, created]));
        // Defer the value selection so the new SelectItem has rendered.
        setTimeout(() => {
          setNewInvoice(prev => ({ ...prev, budget_line_id: created.id }));
        }, 0);
      }
      await refreshBudgetLines();
    } catch (error) {
      console.error('Error creating budget line:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la creazione della voce di costo');
    }
  };

  const handleStartRenameBudgetLine = (line) => {
    setEditingBudgetLineId(line.id);
    setEditingBudgetLineName(line.name);
  };

  const handleSaveRenameBudgetLine = async (line) => {
    const name = editingBudgetLineName.trim();
    if (!name) {
      toast.error('Il nome non può essere vuoto');
      return;
    }
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/budget-lines/${line.id}`, {
        name,
        type: line.type || 'expense',
        order: line.order || 0,
        forecast: line.forecast || 0
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Voce di costo aggiornata');
      setEditingBudgetLineId(null);
      setEditingBudgetLineName('');
      await refreshBudgetLines();
    } catch (error) {
      console.error('Error updating budget line:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la modifica');
    }
  };

  const handleDeleteBudgetLine = async (line) => {
    if (!window.confirm(`Eliminare la voce di costo "${line.name}"?`)) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/budget-lines/${line.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Voce di costo eliminata');
      if (newInvoice.budget_line_id === line.id) {
        setNewInvoice(prev => ({ ...prev, budget_line_id: '' }));
      }
      await refreshBudgetLines();
    } catch (error) {
      console.error('Error deleting budget line:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice status:', error);
    }
  };

  const getStatusBadge = (status, dueDate) => {
    const isOverdue = new Date(dueDate) < new Date() && status === 'pending';
    
    if (isOverdue) {
      return <Badge className="bg-red-100 text-red-800">Scaduta</Badge>;
    }
    
    const statusConfig = {
      pending: { label: 'Da Pagare', className: 'bg-amber-100 text-amber-800' },
      paid: { label: 'Pagata', className: 'bg-emerald-100 text-emerald-800' },
      overdue: { label: 'Scaduta', className: 'bg-red-100 text-red-800' },
      suspended: { label: 'SOSPESA - NON PAGARE', className: 'bg-red-600 text-white font-bold' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Filtri BASE (tutto tranne lo stato): usati anche dalle card riepilogo in alto
  const baseFilteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.description?.toLowerCase().includes(searchTerm.toLowerCase());

    // Invoice type filter
    const invoiceType = invoice.invoice_type || 'passive'; // Default to passive for backward compatibility
    const matchesType = invoiceTypeFilter === 'all' || invoiceType === invoiceTypeFilter;

    // Company filter - match by company name instead of ID
    const invoiceCompany = companies.find(c => c.id === invoice.company_id);
    const matchesCompany = !companyFilter || (invoiceCompany && invoiceCompany.name.toLowerCase().includes(companyFilter.toLowerCase()));

    // Year and month filter
    const invoiceDate = new Date(invoice.due_date);
    const matchesYear = !yearFilter || yearFilter === "all" || invoiceDate.getFullYear().toString() === yearFilter;
    const matchesMonth = !monthFilter || monthFilter === "all" || (invoiceDate.getMonth() + 1).toString() === monthFilter;

    // Without category filter (for admin)
    const matchesCategory = !showWithoutCategory || !invoice.category_id;

    return matchesSearch && matchesType && matchesCompany && matchesYear && matchesMonth && matchesCategory;
  });

  // Totali riepilogo (si adattano ai filtri attivi, tranne il filtro stato)
  const invoiceSummary = (() => {
    const now = new Date();
    const s = {
      all: { count: 0, amount: 0 },
      pending: { count: 0, amount: 0 },
      overdue: { count: 0, amount: 0 },
      paid: { count: 0, amount: 0 },
      suspended: { count: 0, amount: 0 },
    };
    baseFilteredInvoices.forEach(inv => {
      const amt = parseFloat(inv.amount) || 0;
      s.all.count += 1; s.all.amount += amt;
      let key = inv.status;
      if (inv.status === 'pending') {
        key = (inv.due_date && new Date(inv.due_date) < now) ? 'overdue' : 'pending';
      }
      if (s[key]) { s[key].count += 1; s[key].amount += amt; }
    });
    return s;
  })();

  const filteredInvoices = baseFilteredInvoices.filter(invoice => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'overdue') {
      // Scadute: status pending + data scadenza passata
      return invoice.status === 'pending' && invoice.due_date && new Date(invoice.due_date) < new Date();
    }
    if (statusFilter === 'pending') {
      // In Sospeso: status pending + data scadenza NON passata (o senza scadenza)
      return invoice.status === 'pending' && (!invoice.due_date || new Date(invoice.due_date) >= new Date());
    }
    return invoice.status === statusFilter;
  }).sort((a, b) => {
    let aValue, bValue;
    
    if (sortField === 'amount') {
      aValue = parseFloat(a.amount);
      bValue = parseFloat(b.amount);
    } else if (sortField === 'due_date' || sortField === 'created_at') {
      aValue = new Date(a[sortField]);
      bValue = new Date(b[sortField]);
    } else {
      aValue = a[sortField]?.toLowerCase() || '';
      bValue = b[sortField]?.toLowerCase() || '';
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  if (loading) {
    return (
      <Layout title="Gestione Fatture">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestione Fatture">
      <div className="space-y-6 fade-in">
        {/* Header with actions */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Fatture da Pagare</h2>
              <p className="text-sm text-slate-600">Gestisci tutte le fatture in entrata</p>
            </div>
            
            <Dialog open={showNewInvoice} onOpenChange={handleNewInvoiceOpenChange}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="add-invoice-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Fattura
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="new-invoice-dialog" aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle>Nuova Fattura</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateInvoice} className="min-w-0 space-y-4" data-testid="new-invoice-form">
                  {/* Invoice Type Selector */}
                  <div>
                    <Label>Tipo Fattura *</Label>
                    <Select
                      value={newInvoice.invoice_type}
                      onValueChange={(value) => setNewInvoice({...newInvoice, invoice_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passive">
                          <span className="text-red-600">📤 Fattura Passiva (Uscita - da Fornitore)</span>
                        </SelectItem>
                        <SelectItem value="active">
                          <span className="text-green-600">📥 Fattura Attiva (Entrata - a Cliente)</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Natura rapporto (solo fatture passive) */}
                  {newInvoice.invoice_type === 'passive' && (
                    <div>
                      <Label>Natura Rapporto *</Label>
                      <Select
                        value={newInvoice.contact_nature || ''}
                        onValueChange={(v) => setNewInvoice({...newInvoice, contact_nature: v})}
                      >
                        <SelectTrigger data-testid="contact-nature-select">
                          <SelectValue placeholder="Seleziona il tipo di rapporto..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fornitore">🏢 Fornitore</SelectItem>
                          <SelectItem value="partner">🤝 Partner</SelectItem>
                          <SelectItem value="responsabile">🧑‍💼 Responsabile</SelectItem>
                          <SelectItem value="dipendente">👷 Dipendente</SelectItem>
                          <SelectItem value="segnalatore">📣 Segnalatore</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">Classifica il beneficiario: apparirà già pronto nel Pagamento Fatture</p>
                    </div>
                  )}
                  
                  <div className="grid min-w-0 grid-cols-1 md:grid-cols-2 gap-4 [&>div]:min-w-0">
                    <div className="relative">
                      <Label>{newInvoice.invoice_type === 'active' ? 'Cliente' : 'Fornitore / Beneficiario'}</Label>
                      <Input
                        value={newInvoice.supplier}
                        onChange={(e) => { setNewInvoice({...newInvoice, supplier: e.target.value, supplier_iban: '', supplier_email: ''}); setContactMatch(null); }}
                        onFocus={() => contactResults.length > 0 && setShowContactDropdown(true)}
                        onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                        required
                        autoComplete="off"
                        placeholder={newInvoice.invoice_type === 'active' ? 'Nome del cliente' : 'Cerca in rubrica o scrivi un nuovo nominativo...'}
                        data-testid="supplier-input"
                      />
                      {newInvoice.invoice_type === 'passive' && showContactDropdown && contactResults.length > 0 && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="supplier-suggestions">
                          {contactResults.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2 border-b border-slate-100 last:border-0"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setNewInvoice(prev => ({
                                  ...prev,
                                  supplier: c.name,
                                  supplier_iban: '',
                                  contact_nature: ['fornitore','partner','responsabile','dipendente','segnalatore'].includes(c.category) ? c.category : prev.contact_nature,
                                }));
                                setContactMatch(c);
                                setShowContactDropdown(false);
                              }}
                              data-testid={`supplier-suggestion-${c.id}`}
                            >
                              <span className="text-sm truncate">{c.name}</span>
                              <span className={`text-[10px] font-mono shrink-0 ${c.iban ? 'text-slate-400' : 'text-amber-500 font-semibold'}`}>
                                {c.iban ? `${c.iban.slice(0, 12)}…` : 'NO IBAN'}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      {newInvoice.invoice_type === 'passive' && contactMatch && (contactMatch.iban || '').trim() && (
                        <p className="text-xs text-emerald-600 mt-1" data-testid="contact-iban-found">
                          ✓ In rubrica — IBAN: <span className="font-mono">{contactMatch.iban}</span>
                        </p>
                      )}
                    </div>
                    {newInvoice.invoice_type === 'passive' && (newInvoice.supplier || '').trim().length >= 2 && (!contactMatch || !(contactMatch.iban || '').trim()) && (
                      <div>
                        <Label>IBAN Beneficiario {contactMatch ? '(in rubrica senza IBAN)' : ''}</Label>
                        <Input
                          value={newInvoice.supplier_iban || ''}
                          onChange={(e) => setNewInvoice({...newInvoice, supplier_iban: e.target.value.replace(/\s/g, '').toUpperCase()})}
                          placeholder="IT..."
                          className="font-mono text-sm"
                          data-testid="supplier-iban-input"
                        />
                        <p className="text-xs text-amber-600 mt-1">
                          {contactMatch ? 'Verrà aggiunto al contatto in rubrica' : '🆕 Nuovo beneficiario: verrà salvato in rubrica con nome, IBAN e natura'}
                        </p>
                      </div>
                    )}
                    {newInvoice.invoice_type === 'passive' && (newInvoice.supplier || '').trim().length >= 2 && (
                      contactMatch && (contactMatch.email || '').trim() ? (
                        <div>
                          <Label>Email Beneficiario</Label>
                          <p className="text-xs text-emerald-600 mt-2" data-testid="contact-email-found">
                            ✓ {contactMatch.email_source ? 'Da anagrafica' : 'In rubrica'} — {contactMatch.email}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <Label>Email Beneficiario (facoltativa)</Label>
                          <Input
                            type="email"
                            value={newInvoice.supplier_email || ''}
                            onChange={(e) => setNewInvoice({...newInvoice, supplier_email: e.target.value.trim()})}
                            placeholder="es. amministrazione@fornitore.it"
                            data-testid="supplier-email-input"
                          />
                          <p className="text-xs text-slate-500 mt-1">Per la mail di cortesia di avvenuto pagamento — lascia vuota se non serve (es. INPS)</p>
                        </div>
                      )
                    )}
                    <div>
                      <Label>Numero Fattura</Label>
                      <Input
                        value={newInvoice.invoice_number}
                        onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                        required
                        placeholder="Es. FAT-2024-001"
                        data-testid="invoice-number-input"
                      />
                    </div>
                    <div>
                      <Label>Importo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newInvoice.amount}
                        onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                        required
                        data-testid="amount-input"
                      />
                    </div>
                    <div>
                      <Label>Data Emissione *</Label>
                      <Input
                        type="date"
                        value={newInvoice.invoice_date}
                        onChange={(e) => setNewInvoice({...newInvoice, invoice_date: e.target.value})}
                        required
                        data-testid="invoice-date-input"
                      />
                    </div>
                    <div>
                      <Label>Data Scadenza *</Label>
                      <Input
                        type="date"
                        value={newInvoice.due_date}
                        onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                        required
                        data-testid="due-date-input"
                      />
                    </div>
                    <InvoiceCompanySelect companies={companies} loading={companiesLoading} error={companiesError}
                      onRetry={fetchCompanies} value={newInvoice.company_id} onValueChange={handleCompanyChange} />
                    <div>
                      <Label>Stato Pagamento *</Label>
                      <Select 
                        value={newInvoice.status}
                        onValueChange={(value) => setNewInvoice({...newInvoice, status: value})}
                        required
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue placeholder="Seleziona stato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Da Pagare</SelectItem>
                          <SelectItem value="paid">Pagata</SelectItem>
                          <SelectItem value="suspended">Sospesa (NON pagare)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        💡 Se "Pagata", apparirà subito nel Controllo di Gestione
                      </p>
                    </div>
                    <div>
                      <Label>Voce di Costo (Controllo Gestione)</Label>
                      <div className="flex min-w-0 gap-2">
                        <Select 
                          value={newInvoice.budget_line_id}
                          onValueChange={(value) => setNewInvoice({...newInvoice, budget_line_id: value})}
                          disabled={!newInvoice.company_id}
                        >
                          <SelectTrigger data-testid="budget-line-select" className="min-w-0 flex-1 [&>span]:min-w-0 [&>span]:truncate">
                            <SelectValue placeholder={
                              newInvoice.company_id 
                                ? "Opzionale - Seleziona voce di costo" 
                                : "Prima seleziona una società"
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredBudgetLines.map(line => (
                              <SelectItem key={line.id} value={line.id}>
                                {line.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          className="shrink-0 px-3"
                          title="Gestisci voci di costo"
                          disabled={!newInvoice.company_id}
                          onClick={() => setShowNewCategory(true)}
                          data-testid="manage-budget-lines-btn"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Clicca + per aggiungere o gestire le voci di costo della società
                      </p>
                    </div>
                    <div>
                      <Label>Immobile (opzionale)</Label>
                      <SearchableSelect
                        value={newInvoice.property_id || ""}
                        onValueChange={(value) => setNewInvoice({...newInvoice, property_id: value})}
                        options={[
                          { value: "", label: "Nessuna associazione" },
                          ...properties.map(property => ({
                            value: property.id,
                            label: `${property.name} - ${property.city} (${property.property_code || 'N/A'})`
                          }))
                        ]}
                        placeholder="Cerca immobile..."
                        searchPlaceholder="Cerca per nome, città o codice..."
                      />
                    </div>
                    <div className="col-span-full">
                      <Label>Giustificativo (Allega copia fattura)</Label>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setAttachmentFile(e.target.files[0])}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {attachmentFile ? `✓ File selezionato: ${attachmentFile.name}` : 'PDF, JPG o PNG (opzionale)'}
                      </p>
                    </div>
                    <div className="col-span-full">
                      <Label>Descrizione</Label>
                      <Input
                        value={newInvoice.description}
                        onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                        placeholder="Descrizione della fattura"
                        data-testid="description-input"
                      />
                    </div>

                    {/* Costo Fisso Ricorrente */}
                    {newInvoice.invoice_type === 'passive' && (
                      <div className="col-span-full border-t pt-3 mt-1">
                        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="recurring-toggle">
                          <input
                            type="checkbox"
                            checked={newInvoice.is_recurring || false}
                            onChange={(e) => setNewInvoice({...newInvoice, is_recurring: e.target.checked})}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm font-medium text-slate-700">Costo Fisso Ricorrente</span>
                          <span className="text-xs text-slate-400">(verrà generato automaticamente ogni mese)</span>
                        </label>

                        {newInvoice.is_recurring && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                            <div>
                              <Label className="text-xs">IBAN Beneficiario</Label>
                              <Input
                                value={newInvoice.recurring_iban || ''}
                                onChange={(e) => setNewInvoice({...newInvoice, recurring_iban: e.target.value.toUpperCase()})}
                                placeholder="IT..."
                                className="font-mono text-sm"
                                data-testid="recurring-iban"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Intestatario Conto (se diverso da fornitore)</Label>
                              <Input
                                value={newInvoice.recurring_xml_name || ''}
                                onChange={(e) => setNewInvoice({...newInvoice, recurring_xml_name: e.target.value})}
                                placeholder="Es. Officina Arredo di Sonia Alpi"
                                className="text-sm"
                                data-testid="recurring-xml-name"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Giorno scadenza mensile</Label>
                              <Input
                                type="number"
                                min="1"
                                max="28"
                                value={newInvoice.recurring_day || 10}
                                onChange={(e) => setNewInvoice({...newInvoice, recurring_day: Number(e.target.value)})}
                                className="text-sm"
                                data-testid="recurring-day"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Tipo beneficiario</Label>
                              <Select
                                value={newInvoice.recurring_category || 'fornitore'}
                                onValueChange={(v) => setNewInvoice({...newInvoice, recurring_category: v})}
                              >
                                <SelectTrigger className="text-sm" data-testid="recurring-category">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fornitore">Fornitore</SelectItem>
                                  <SelectItem value="partner">Partner</SelectItem>
                                  <SelectItem value="responsabile">Responsabile</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewInvoice(false)}
                      data-testid="cancel-new-invoice-button"
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-blue-900 hover:bg-blue-800"
                      data-testid="save-invoice-btn"
                    >
                      Crea Fattura
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Manage Budget Lines (Voci di Costo) Dialog */}
        <Dialog
          open={showNewCategory}
          onOpenChange={(open) => {
            setShowNewCategory(open);
            if (!open) {
              setEditingBudgetLineId(null);
              setEditingBudgetLineName('');
              setNewBudgetLineName('');
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Voci di Costo{(() => {
                  const c = companies.find(co => co.id === newInvoice.company_id);
                  return c ? ` — ${c.name}` : '';
                })()}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <form onSubmit={handleCreateBudgetLine} className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>Nuova voce di costo *</Label>
                  <Input
                    value={newBudgetLineName}
                    onChange={(e) => setNewBudgetLineName(e.target.value)}
                    placeholder="Es. Manutenzione straordinaria"
                    data-testid="budget-line-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="save-budget-line-btn"
                >
                  Aggiungi
                </Button>
              </form>

              <div className="border-t pt-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Voci esistenti</p>
                {filteredBudgetLines.length === 0 ? (
                  <p className="text-sm text-slate-500">Nessuna voce di costo per questa società.</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="budget-lines-list">
                    {filteredBudgetLines.map(line => (
                      <div key={line.id} className="flex items-center gap-2 bg-slate-50 rounded px-3 py-2">
                        {editingBudgetLineId === line.id ? (
                          <>
                            <Input
                              value={editingBudgetLineName}
                              onChange={(e) => setEditingBudgetLineName(e.target.value)}
                              className="flex-1 h-8"
                              data-testid={`budget-line-edit-input-${line.id}`}
                            />
                            <Button
                              type="button"
                              size="sm"
                              className="bg-green-700 hover:bg-green-600 h-8"
                              onClick={() => handleSaveRenameBudgetLine(line)}
                              data-testid={`budget-line-save-${line.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => {
                                setEditingBudgetLineId(null);
                                setEditingBudgetLineName('');
                              }}
                            >
                              Annulla
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-sm text-slate-800">{line.name}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2"
                              title="Rinomina"
                              onClick={() => handleStartRenameBudgetLine(line)}
                              data-testid={`budget-line-rename-${line.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-red-600 hover:text-red-700"
                              title="Elimina"
                              onClick={() => handleDeleteBudgetLine(line)}
                              data-testid={`budget-line-delete-${line.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setShowNewCategory(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={showEditInvoice} onOpenChange={setShowEditInvoice}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="edit-invoice-dialog" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Modifica Fattura</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateInvoice} className="min-w-0 space-y-4" data-testid="edit-invoice-form">
              {/* Invoice Type Selector */}
              <div>
                <Label>Tipo Fattura *</Label>
                <Select
                  value={newInvoice.invoice_type}
                  onValueChange={(value) => setNewInvoice({...newInvoice, invoice_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passive">
                      <span className="text-red-600">📤 Fattura Passiva (Uscita - da Fornitore)</span>
                    </SelectItem>
                    <SelectItem value="active">
                      <span className="text-green-600">📥 Fattura Attiva (Entrata - a Cliente)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Natura rapporto (solo fatture passive) */}
              {newInvoice.invoice_type === 'passive' && (
                <div>
                  <Label>Natura Rapporto *</Label>
                  <Select
                    value={newInvoice.contact_nature || ''}
                    onValueChange={(v) => setNewInvoice({...newInvoice, contact_nature: v})}
                  >
                    <SelectTrigger data-testid="contact-nature-select-edit">
                      <SelectValue placeholder="Seleziona il tipo di rapporto..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fornitore">🏢 Fornitore</SelectItem>
                      <SelectItem value="partner">🤝 Partner</SelectItem>
                      <SelectItem value="responsabile">🧑‍💼 Responsabile</SelectItem>
                      <SelectItem value="dipendente">👷 Dipendente</SelectItem>
                      <SelectItem value="segnalatore">📣 Segnalatore</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Classifica il beneficiario: apparirà già pronto nel Pagamento Fatture</p>
                </div>
              )}
              
              <div className="grid min-w-0 grid-cols-1 md:grid-cols-2 gap-4 [&>div]:min-w-0">
                <div className="relative">
                  <Label>{newInvoice.invoice_type === 'active' ? 'Cliente' : 'Fornitore / Beneficiario'}</Label>
                  <Input
                    value={newInvoice.supplier}
                    onChange={(e) => { setNewInvoice({...newInvoice, supplier: e.target.value, supplier_iban: '', supplier_email: ''}); setContactMatch(null); }}
                    onFocus={() => contactResults.length > 0 && setShowContactDropdown(true)}
                    onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                    required
                    autoComplete="off"
                    placeholder={newInvoice.invoice_type === 'active' ? 'Nome del cliente' : 'Cerca in rubrica o scrivi un nuovo nominativo...'}
                    data-testid="supplier-input-edit"
                  />
                  {newInvoice.invoice_type === 'passive' && showContactDropdown && contactResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="supplier-suggestions-edit">
                      {contactResults.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between gap-2 border-b border-slate-100 last:border-0"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setNewInvoice(prev => ({
                              ...prev,
                              supplier: c.name,
                              supplier_iban: '',
                              contact_nature: ['fornitore','partner','responsabile','dipendente','segnalatore'].includes(c.category) ? c.category : prev.contact_nature,
                            }));
                            setContactMatch(c);
                            setShowContactDropdown(false);
                          }}
                        >
                          <span className="text-sm truncate">{c.name}</span>
                          <span className={`text-[10px] font-mono shrink-0 ${c.iban ? 'text-slate-400' : 'text-amber-500 font-semibold'}`}>
                            {c.iban ? `${c.iban.slice(0, 12)}…` : 'NO IBAN'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {newInvoice.invoice_type === 'passive' && contactMatch && (contactMatch.iban || '').trim() && (
                    <p className="text-xs text-emerald-600 mt-1" data-testid="contact-iban-found-edit">
                      ✓ In rubrica — IBAN: <span className="font-mono">{contactMatch.iban}</span>
                    </p>
                  )}
                </div>
                {newInvoice.invoice_type === 'passive' && (newInvoice.supplier || '').trim().length >= 2 && (!contactMatch || !(contactMatch.iban || '').trim()) && (
                  <div>
                    <Label>IBAN Beneficiario {contactMatch ? '(in rubrica senza IBAN)' : ''}</Label>
                    <Input
                      value={newInvoice.supplier_iban || ''}
                      onChange={(e) => setNewInvoice({...newInvoice, supplier_iban: e.target.value.replace(/\s/g, '').toUpperCase()})}
                      placeholder="IT..."
                      className="font-mono text-sm"
                      data-testid="supplier-iban-input-edit"
                    />
                    <p className="text-xs text-amber-600 mt-1">
                      {contactMatch ? 'Verrà aggiunto al contatto in rubrica' : '🆕 Nuovo beneficiario: verrà salvato in rubrica con nome, IBAN e natura'}
                    </p>
                  </div>
                )}
                {newInvoice.invoice_type === 'passive' && (newInvoice.supplier || '').trim().length >= 2 && (
                  contactMatch && (contactMatch.email || '').trim() ? (
                    <div>
                      <Label>Email Beneficiario</Label>
                      <p className="text-xs text-emerald-600 mt-2" data-testid="contact-email-found-edit">
                        ✓ {contactMatch.email_source ? 'Da anagrafica' : 'In rubrica'} — {contactMatch.email}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label>Email Beneficiario (facoltativa)</Label>
                      <Input
                        type="email"
                        value={newInvoice.supplier_email || ''}
                        onChange={(e) => setNewInvoice({...newInvoice, supplier_email: e.target.value.trim()})}
                        placeholder="es. amministrazione@fornitore.it"
                        data-testid="supplier-email-input-edit"
                      />
                      <p className="text-xs text-slate-500 mt-1">Per la mail di cortesia di avvenuto pagamento — lascia vuota se non serve (es. INPS)</p>
                    </div>
                  )
                )}
                <div>
                  <Label>Numero Fattura</Label>
                  <Input
                    value={newInvoice.invoice_number}
                    onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                    required
                    placeholder="Es. FAT-2024-001"
                    data-testid="invoice-number-input-edit"
                  />
                </div>
                <div>
                  <Label>Importo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newInvoice.amount}
                    onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})}
                    required
                    data-testid="amount-input-edit"
                  />
                </div>
                <div>
                  <Label>Data Emissione *</Label>
                  <Input
                    type="date"
                    value={newInvoice.invoice_date}
                    onChange={(e) => setNewInvoice({...newInvoice, invoice_date: e.target.value})}
                    required
                    data-testid="invoice-date-input-edit"
                  />
                </div>
                <div>
                  <Label>Data Scadenza *</Label>
                  <Input
                    type="date"
                    value={newInvoice.due_date}
                    onChange={(e) => setNewInvoice({...newInvoice, due_date: e.target.value})}
                    required
                    data-testid="due-date-input-edit"
                  />
                </div>
                <InvoiceCompanySelect companies={companies} loading={companiesLoading} error={companiesError}
                  onRetry={fetchCompanies} value={newInvoice.company_id} onValueChange={handleCompanyChange} editing />
                <div>
                  <Label>Stato Pagamento *</Label>
                  <Select 
                    value={newInvoice.status}
                    onValueChange={(value) => setNewInvoice({...newInvoice, status: value})}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Da Pagare</SelectItem>
                      <SelectItem value="paid">Pagata</SelectItem>
                      <SelectItem value="suspended">Sospesa (NON pagare)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Voce di Costo (Controllo Gestione)</Label>
                  <div className="flex min-w-0 gap-2">
                    <Select 
                      value={newInvoice.budget_line_id}
                      onValueChange={(value) => setNewInvoice({...newInvoice, budget_line_id: value})}
                      disabled={!newInvoice.company_id}
                    >
                      <SelectTrigger className="min-w-0 flex-1 [&>span]:min-w-0 [&>span]:truncate" data-testid="budget-line-select-edit">
                        <SelectValue placeholder={
                          newInvoice.company_id 
                            ? "Opzionale - Seleziona voce di costo" 
                            : "Prima seleziona una società"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredBudgetLines.map(line => (
                          <SelectItem key={line.id} value={line.id}>
                            {line.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 px-3"
                      title="Gestisci voci di costo"
                      disabled={!newInvoice.company_id}
                      onClick={() => setShowNewCategory(true)}
                      data-testid="manage-budget-lines-btn-edit"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Immobile (opzionale)</Label>
                  <SearchableSelect
                    value={newInvoice.property_id || ""}
                    onValueChange={(value) => setNewInvoice({...newInvoice, property_id: value})}
                    options={[
                      { value: "", label: "Nessuna associazione" },
                      ...properties.map(property => ({
                        value: property.id,
                        label: `${property.name} - ${property.city} (${property.property_code || 'N/A'})`
                      }))
                    ]}
                    placeholder="Cerca immobile..."
                    searchPlaceholder="Cerca per nome, città o codice..."
                  />
                </div>
                <div className="col-span-full">
                  <Label>Giustificativo (Allega copia fattura)</Label>
                  {editingInvoice?.attachment_path && !attachmentFile && (
                    <div className="flex items-center gap-2 mb-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                      <span className="text-emerald-700 text-sm font-medium">Giustificativo gia caricato</span>
                      <span className="text-emerald-600 text-xs">(seleziona un nuovo file solo se vuoi sostituirlo)</span>
                    </div>
                  )}
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setAttachmentFile(e.target.files[0])}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {attachmentFile ? `Nuovo file: ${attachmentFile.name}` : editingInvoice?.attachment_path ? 'Seleziona un file solo per sostituire il giustificativo esistente' : 'PDF, JPG o PNG (opzionale)'}
                  </p>
                </div>
                <div className="col-span-full">
                  <Label>Descrizione</Label>
                  <Input
                    value={newInvoice.description}
                    onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                    placeholder="Descrizione della fattura"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditInvoice(false)}
                  data-testid="cancel-edit-invoice-button"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800"
                  data-testid="update-invoice-button"
                >
                  Aggiorna Fattura
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Riepilogo totali — cliccabili, si adattano ai filtri attivi */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { key: 'all', label: 'Totale Fatture', card: 'border-blue-200 bg-blue-50', text: 'text-blue-900', ring: 'ring-blue-400' },
            { key: 'paid', label: 'Totale Pagato', card: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-800', ring: 'ring-emerald-400' },
            { key: 'pending', label: 'Da Pagare', card: 'border-amber-200 bg-amber-50', text: 'text-amber-800', ring: 'ring-amber-400' },
            { key: 'overdue', label: 'Scadute', card: 'border-red-200 bg-red-50', text: 'text-red-700', ring: 'ring-red-400' },
            { key: 'suspended', label: 'Sospese', card: 'border-slate-300 bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-400' },
          ].map(c => (
            <Card
              key={c.key}
              onClick={() => setStatusFilter(statusFilter === c.key ? 'all' : c.key)}
              data-testid={`invoice-summary-${c.key}`}
              className={`p-4 border-2 shadow-md cursor-pointer transition-transform hover:scale-[1.02] ${c.card} ${statusFilter === c.key ? `ring-2 ring-offset-1 ${c.ring}` : ''}`}
            >
              <div className={`text-xs font-semibold uppercase tracking-wide ${c.text}`}>{c.label}</div>
              <div className={`text-xl font-bold mt-1 ${c.text}`} data-testid={`invoice-summary-${c.key}-amount`}>
                € {invoiceSummary[c.key].amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{invoiceSummary[c.key].count} fattur{invoiceSummary[c.key].count === 1 ? 'a' : 'e'}</div>
            </Card>
          ))}
        </div>

        {/* Year Selector */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-slate-700">Filtra per Anno:</div>
            <YearSelector currentYear={currentYear} onChange={setCurrentYear} />
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca fornitore, numero, descrizione..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-invoices-input"
                />
              </div>
            </div>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Filtra per società..."
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="pl-10"
              />
            </div>

            <div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter-select">
                  <SelectValue placeholder="Tutti gli stati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="pending">Da Pagare</SelectItem>
                  <SelectItem value="paid">Pagate</SelectItem>
                  <SelectItem value="overdue">Scadute</SelectItem>
                  <SelectItem value="suspended">Sospese (non pagare)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={invoiceTypeFilter} onValueChange={setInvoiceTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="passive">📤 Passive (Uscite)</SelectItem>
                  <SelectItem value="active">📥 Attive (Entrate)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={yearFilter || "all"} onValueChange={(value) => setYearFilter(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti gli anni" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli anni</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={monthFilter || "all"} onValueChange={(value) => setMonthFilter(value === "all" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tutti i mesi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i mesi</SelectItem>
                  <SelectItem value="1">Gennaio</SelectItem>
                  <SelectItem value="2">Febbraio</SelectItem>
                  <SelectItem value="3">Marzo</SelectItem>
                  <SelectItem value="4">Aprile</SelectItem>
                  <SelectItem value="5">Maggio</SelectItem>
                  <SelectItem value="6">Giugno</SelectItem>
                  <SelectItem value="7">Luglio</SelectItem>
                  <SelectItem value="8">Agosto</SelectItem>
                  <SelectItem value="9">Settembre</SelectItem>
                  <SelectItem value="10">Ottobre</SelectItem>
                  <SelectItem value="11">Novembre</SelectItem>
                  <SelectItem value="12">Dicembre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Admin-only: Show invoices without category */}
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="without-category"
              checked={showWithoutCategory}
              onChange={(e) => setShowWithoutCategory(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="without-category" className="text-sm text-slate-700">
              🔍 Mostra solo fatture senza voce di costo (da assegnare)
            </label>
          </div>
        </Card>

        {/* Invoices list */}
        <div className="grid gap-4">
          {filteredInvoices.length === 0 ? (
            <Card className="p-8 text-center border-0 shadow-lg">
              <FileText className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Nessuna fattura trovata</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Prova a modificare i filtri di ricerca' 
                  : 'Inizia inserendo la prima fattura'
                }
              </p>
            </Card>
          ) : (
            filteredInvoices.map((invoice) => {
              const isSuspended = invoice.status === 'suspended';
              return (
              <Card key={invoice.id}
                className={`p-6 border-2 shadow-lg card-hover ${isSuspended ? 'bg-red-50 border-red-500 ring-2 ring-red-200' : 'border-0'}`}
                data-testid={`invoice-card-${invoice.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className={`text-lg font-semibold ${isSuspended ? 'text-red-900' : 'text-slate-900'}`}>{invoice.supplier}</h3>
                      {getStatusBadge(invoice.status, invoice.due_date)}
                      {new Date(invoice.due_date) < new Date() && invoice.status === 'pending' && (
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <p className={`text-sm ${isSuspended ? 'text-red-800' : 'text-slate-600'}`}>
                      <span className="font-medium">Fattura:</span> {invoice.invoice_number}
                      {/* Show company */}
                      {invoice.company_id && (() => {
                        const company = companies.find(c => c.id === invoice.company_id);
                        return company ? (
                          <span className="ml-3">
                            <span className="font-medium">Società:</span> {company.name}
                          </span>
                        ) : null;
                      })()}
                      {/* Show category */}
                      {invoice.category_id && (() => {
                        const category = categories.find(cat => cat.id === invoice.category_id);
                        return category ? (
                          <span className="ml-3">
                            <span className="font-medium">Categoria:</span> {category.name}
                          </span>
                        ) : null;
                      })()}
                      {/* Legacy category field for backward compatibility */}
                      {invoice.category && !invoice.category_id && (
                        <span className="ml-3">
                          <span className="font-medium">Categoria:</span> {invoice.category}
                        </span>
                      )}
                    </p>
                    <div className={`text-sm ${isSuspended ? 'text-red-700' : 'text-slate-500'}`}>
                      <span className="font-medium">Importo:</span> €{invoice.amount.toLocaleString('it-IT')} | 
                      <span className="font-medium ml-2">Scadenza:</span> {new Date(invoice.due_date).toLocaleDateString('it-IT')}
                    </div>
                    {invoice.description && (
                      <p className={`text-sm ${isSuspended ? 'text-red-700' : 'text-slate-500'}`}>{invoice.description}</p>
                    )}
                    {isSuspended && (
                      <p className="text-xs text-red-700 font-semibold flex items-center gap-1 mt-1">
                        <AlertTriangle className="h-4 w-4" />
                        Fattura sospesa: in attesa di controlli/storni. NON PAGARE.
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {/* Edit Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={() => handleEditInvoice(invoice)}
                      data-testid={`edit-invoice-${invoice.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Modifica
                    </Button>
                    
                    {/* Delete Button (Admin + Operatore Fatture) */}
                    {(user?.role === 'admin' || user?.role === 'invoice_operator') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        data-testid={`delete-invoice-${invoice.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Elimina
                      </Button>
                    )}
                    
                    {/* Upload Attachment Button (if no attachment) */}
                    {!invoice.attachment_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        onClick={() => {
                          setEditingInvoice(invoice);
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.jpg,.jpeg,.png';
                          input.onchange = async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              try {
                                const token = localStorage.getItem('hw_token');
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                await axios.post(`${API}/invoices/${invoice.id}/upload-attachment`, formData, {
                                  headers: { 
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                
                                alert('Giustificativo caricato con successo!');
                                fetchInvoices();
                              } catch (error) {
                                console.error('Error uploading attachment:', error);
                                alert('Errore nel caricamento del giustificativo');
                              }
                            }
                          };
                          input.click();
                        }}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Carica Giustificativo
                      </Button>
                    )}
                    
                    {/* View Attachment Button (if attachment exists) */}
                    {invoice.attachment_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('hw_token');
                            const response = await axios.get(`${API}/invoices/${invoice.id}/download-attachment`, {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob'
                            });
                            
                            // Get content type from response
                            const contentType = response.headers['content-type'] || 'application/pdf';
                            
                            // Create blob with correct MIME type
                            const blob = new Blob([response.data], { type: contentType });
                            const url = window.URL.createObjectURL(blob);
                            
                            // Open in new tab instead of downloading
                            window.open(url, '_blank');
                          } catch (error) {
                            console.error('Error viewing attachment:', error);
                            const errorMessage = error.response?.data?.detail || 'Errore nell\'apertura del giustificativo. Il file potrebbe essere stato eliminato durante un deploy.';
                            alert(errorMessage);
                          }
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Vedi Giustificativo
                      </Button>
                    )}

                    {/* Upload Payment Receipt Button (ricevuta di pagamento) */}
                    {!invoice.payment_receipt_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        data-testid={`upload-receipt-${invoice.id}`}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,image/*,application/pdf';
                          input.onchange = async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            try {
                              const token = localStorage.getItem('hw_token');
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await axios.post(`${API}/invoices/${invoice.id}/upload-payment-receipt`, formData, {
                                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                              });
                              alert(res.data?.auto_paid ? 'Ricevuta caricata — fattura segnata PAGATA ✓' : 'Ricevuta di pagamento caricata!');
                              fetchInvoices();
                            } catch (error) {
                              alert(error.response?.data?.detail || 'Errore nel caricamento della ricevuta');
                            }
                          };
                          input.click();
                        }}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Carica Ricevuta
                      </Button>
                    )}

                    {/* View Payment Receipt Button */}
                    {invoice.payment_receipt_path && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        data-testid={`view-receipt-${invoice.id}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('hw_token');
                            const response = await axios.get(`${API}/invoices/${invoice.id}/download-payment-receipt`, {
                              headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
                            });
                            const contentType = response.headers['content-type'] || 'application/pdf';
                            const blob = new Blob([response.data], { type: contentType });
                            window.open(window.URL.createObjectURL(blob), '_blank');
                          } catch (error) {
                            alert(error.response?.data?.detail || 'Errore nell\'apertura della ricevuta');
                          }
                        }}
                      >
                        <Receipt className="h-4 w-4 mr-1" />
                        Vedi Ricevuta
                      </Button>
                    )}

                    {/* Mark as Paid Button */}
                    {invoice.status === 'pending' && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => updateInvoiceStatus(invoice.id, 'paid')}
                        data-testid={`mark-paid-${invoice.id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Segna come Pagata
                      </Button>
                    )}

                    {/* Mark as Suspended Button (da Pending → Sospesa) */}
                    {invoice.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-400 text-red-700 hover:bg-red-50"
                        onClick={() => {
                          if (window.confirm('Vuoi sospendere questa fattura?\n\nLa fattura non sarà segnalata come da pagare finché non la riattivi.')) {
                            updateInvoiceStatus(invoice.id, 'suspended');
                          }
                        }}
                        data-testid={`mark-suspended-${invoice.id}`}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Sospendi
                      </Button>
                    )}

                    {/* Suspended Status Display + Riattiva */}
                    {invoice.status === 'suspended' && (
                      <div className="flex items-center gap-3">
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => updateInvoiceStatus(invoice.id, 'pending')}
                          data-testid={`unsuspend-${invoice.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Riattiva (Da Pagare)
                        </Button>
                      </div>
                    )}
                    
                    {/* Paid Status Display */}
                    {invoice.status === 'paid' && invoice.paid_at && (
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-emerald-600">
                          <span className="font-medium">Pagata il:</span> {new Date(invoice.paid_at).toLocaleDateString('it-IT')}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={() => updateInvoiceStatus(invoice.id, 'pending')}
                          data-testid={`revert-pending-${invoice.id}`}
                        >
                          Riporta a Da Pagare
                        </Button>
                      </div>
                    )}
                    {invoice.status === 'paid' && !invoice.paid_at && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-emerald-600 font-medium">Pagata</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={() => updateInvoiceStatus(invoice.id, 'pending')}
                          data-testid={`revert-pending-${invoice.id}`}
                        >
                          Riporta a Da Pagare
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default InvoicesManager;